import { apiFetch } from '../config/api'
import type { MachineType, WeldSample } from './weldTxtParser'

export interface UploadWeldTelemetryInput {
  projectId: string
  pipeSetId: string
  weldId: string
  machineType: MachineType
  samples: WeldSample[]
  passMappings: Record<string, string>
  /**
   * Deprecated single-file label. New callers should pass `sourceFilenames`
   * and let the server compact the list — kept here for back-compat with
   * existing single-file tests + single-file call sites.
   */
  sourceFilename: string
  /**
   * Full list of TXT filenames whose samples are unioned into this batch
   * (issue 06). The server stores this in `weld_telemetry_uploads.source_filenames`.
   */
  sourceFilenames?: string[]
  /**
   * `'merge'` for multi-file batches (and any time the caller wants
   * INSERT-IGNORE semantics against prior samples); `'replace'` for the
   * historical "blow away the prior upload" flow. Defaults to `'replace'`
   * server-side when omitted.
   */
  mode?: 'replace' | 'merge'
  jobNumber: string | null
  weldNumber: string | null
}

export interface UploadWeldTelemetryResult {
  uploadId: string
  totalRecords: number
  /** Echoes the resolved mode the server actually took (issue 05). */
  mode?: 'replace' | 'merge'
}

/**
 * Send a bulk weld-telemetry upload to the server. Mirrors the contract from
 * server/src/routes/weldTelemetry.ts (issue 01) plus the merge/multi-file
 * additions from floorsheet-multi-txt-upload issues 05 + 06.
 */
export async function uploadWeldTelemetry(
  input: UploadWeldTelemetryInput
): Promise<UploadWeldTelemetryResult> {
  const path = telemetryBasePath(input) + '/upload'

  return apiFetch<UploadWeldTelemetryResult>(path, {
    method: 'POST',
    body: JSON.stringify({
      machineType: input.machineType,
      samples: input.samples,
      passMappings: input.passMappings,
      sourceFilename: input.sourceFilename,
      sourceFilenames: input.sourceFilenames,
      mode: input.mode,
      jobNumber: input.jobNumber,
      weldNumber: input.weldNumber
    })
  })
}

export interface ExistingUploadSummary {
  sourceFilename: string
  uploadedAt: string
  passMappings: Record<string, string>
}

export type ExistingWeldDataSource = 'upload' | 'manual'

export interface FetchExistingWeldTelemetryResult {
  existingUpload: ExistingUploadSummary | null
  source: ExistingWeldDataSource | null
}

/**
 * Fetch any existing weld telemetry upload for a weld, plus the current data
 * source on the WeldParameters page (issue 07).
 */
export async function fetchExistingWeldTelemetry(input: {
  projectId: string
  pipeSetId: string
  weldId: string
}): Promise<FetchExistingWeldTelemetryResult> {
  const path = telemetryBasePath(input) + '/existing'
  return apiFetch<FetchExistingWeldTelemetryResult>(path, { method: 'GET' })
}

/**
 * Permanently delete the uploaded TXT telemetry for a weld. Cascades to all
 * per-sample rows via the FK in 020_create_weld_telemetry_samples.sql.
 */
export async function deleteWeldTelemetry(input: {
  projectId: string
  pipeSetId: string
  weldId: string
}): Promise<{ deleted: number }> {
  return apiFetch<{ deleted: number }>(telemetryBasePath(input), {
    method: 'DELETE'
  })
}

export interface WeldTelemetrySampleRow {
  sampleId: number
  evtNo: number
  sampleTime: number
  startTime: number | null
  /**
   * Wall-clock UTC ISO timestamp for this sample, e.g.
   * `'2026-05-01T17:08:34.000Z'`. Derived by the TXT parser from the
   * `Year/Month/Day/Hour/Minute/Second` columns and rendered as
   * `DD-MMM-YYYY, h:mm:ss A` in the Pass Analytics table.
   * `null` for legacy uploads that pre-date this capture.
   */
  recordedAt: string | null
  duration: number | null
  torch: 'Lead' | 'Trail'
  /** CW or CCW from Data360 bug_type or TXT filename. */
  bugType?: 'CW' | 'CCW' | null
  tilt: number | null
  distance: number | null
  travelSpeed: number | null
  volts: number | null
  amps: number | null
  wireIpm: number | null
  widthMil: number | null
  targetVlt: number | null
  csBias: number | null
  bpm: number | null
  totWireIn: number | null
  trueEnergy: number | null
  rprtHeat: number | null
  calcHeat: number | null
  /**
   * 0-based position of this sample's source TXT inside the upload batch
   * that produced it. Multi-file uploads carry one entry per parsed file
   * (matching `FetchWeldTelemetrySamplesResult.sourceFilenames`); legacy
   * single-file uploads come back with `null` for every row. Optional so
   * historic test fixtures and any non-multi-file callers stay valid.
   */
  sourceFileIndex?: number | null
}

export interface FetchWeldTelemetrySamplesResult {
  weldId: string
  passLabel: string
  sampleCount: number
  samples: WeldTelemetrySampleRow[]
  /**
   * Filenames of the TXT files that were merged into the upload, in the
   * same order the user picked them. `samples[i].sourceFileIndex` indexes
   * into this array. Empty array for legacy uploads that didn't capture
   * a multi-file manifest.
   */
  sourceFilenames: string[]
}

/**
 * Fetch every sample row for one (weld, pass) combination from the server.
 * Server-side ordering is `sample_time ASC`; the response is passed through
 * unchanged. The Pass Analytics page consumes this for both the Table tab
 * and the Graph tab (one fetch shared between both).
 */
export async function fetchWeldTelemetrySamples(input: {
  projectId: string
  pipeSetId: string
  weldId: string
  passLabel: string
}): Promise<FetchWeldTelemetrySamplesResult> {
  const path =
    telemetryBasePath(input) +
    `/samples?pass=${encodeURIComponent(input.passLabel)}`
  const result = await apiFetch<FetchWeldTelemetrySamplesResult>(path, { method: 'GET' })
  // Tolerate older servers that pre-date the multi-file manifest. The
  // table renders no divider when this array is empty, which is the
  // intended behavior for legacy single-file uploads.
  return {
    ...result,
    sourceFilenames: Array.isArray(result.sourceFilenames) ? result.sourceFilenames : []
  }
}

function telemetryBasePath(input: {
  projectId: string
  pipeSetId: string
  weldId: string
}): string {
  return (
    `/api/projects/${encodeURIComponent(input.projectId)}` +
    `/pipe-sets/${encodeURIComponent(input.pipeSetId)}` +
    `/welds/${encodeURIComponent(input.weldId)}` +
    `/telemetry`
  )
}

// ---------------------------------------------------------------------------
// Aggregate (server-computed Min/Avg/Max per pass + parameter)
// ---------------------------------------------------------------------------

export interface AggregateMinAvgMax {
  min: number | null
  avg: number | null
  max: number | null
}

export interface AggregateMinAvgMaxOverride extends AggregateMinAvgMax {
  sampleCount: number
}

export interface AggregateRowResponse {
  pass: string
  sampleCount: number
  /** Sorted set of torches that contributed to this pass. */
  torches: ('Lead' | 'Trail')[]
  /**
   * Base Min/Avg/Max for every aggregate parameter. Keys mirror
   * `STATS_COLUMN_KEYS` plus `tilt` and `distance` for the WeldParameters
   * uploaded view.
   */
  values: Record<string, AggregateMinAvgMax>
  /**
   * User-curated overrides (from Pass Analytics → Calculate min/max). Keys
   * are parameter names; consumers should prefer override values over
   * `values[key]` when both are present.
   */
  overrides: Record<string, AggregateMinAvgMaxOverride>
}

export interface FetchWeldTelemetryAggregateResult {
  uploadId: string
  uploadedAt: string
  sourceFilename: string
  passMappings: Record<string, string>
  rows: AggregateRowResponse[]
}

/**
 * Fetch the server-computed Min/Avg/Max aggregate for the latest upload on a
 * weld. The server pre-merges any user-curated overrides into the response so
 * the WeldParameters page can hydrate without a second round-trip. Throws if
 * no upload exists.
 */
export async function fetchWeldTelemetryAggregate(input: {
  projectId: string
  pipeSetId: string
  weldId: string
}): Promise<FetchWeldTelemetryAggregateResult> {
  const path = telemetryBasePath(input) + '/aggregate'
  return apiFetch<FetchWeldTelemetryAggregateResult>(path, { method: 'GET' })
}

export interface BugTypesByPassRow {
  pass: string
  bugTypes: Array<'CW' | 'CCW'>
}

export interface FetchBugTypesByPassResult {
  uploadId: string
  passes: BugTypesByPassRow[]
}

/** Distinct CW/CCW labels per mapped pass for the latest upload. */
export async function fetchBugTypesByPass(input: {
  projectId: string
  pipeSetId: string
  weldId: string
}): Promise<FetchBugTypesByPassResult> {
  const path = telemetryBasePath(input) + '/bug-types-by-pass'
  return apiFetch<FetchBugTypesByPassResult>(path, { method: 'GET' })
}

export interface RootBugTypeAggregateMinAvgMax {
  min: number | null
  avg: number | null
  max: number | null
}

export interface RootBugTypeAggregateRow {
  bugType: 'CW' | 'CCW'
  present: boolean
  sampleCount: number
  values: Record<string, RootBugTypeAggregateMinAvgMax>
}

export interface FetchRootBugTypeAggregateResult {
  uploadId: string
  rows: [RootBugTypeAggregateRow, RootBugTypeAggregateRow]
}

/** Root pass Min/Avg/Max split by CW vs CCW for the latest upload. */
export async function fetchRootBugTypeAggregate(input: {
  projectId: string
  pipeSetId: string
  weldId: string
}): Promise<FetchRootBugTypeAggregateResult> {
  const path = telemetryBasePath(input) + '/root-bug-type-aggregate'
  return apiFetch<FetchRootBugTypeAggregateResult>(path, { method: 'GET' })
}

/**
 * Apply pass name mappings to an existing telemetry upload (Data360 rename pass).
 */
export async function remapPassMappings(input: {
  projectId: string
  pipeSetId: string
  weldId: string
  passMappings: Record<string, string>
}): Promise<FetchWeldTelemetryAggregateResult> {
  const path = telemetryBasePath(input) + '/remap-pass-mappings'
  return apiFetch<FetchWeldTelemetryAggregateResult>(path, {
    method: 'POST',
    body: JSON.stringify({ passMappings: input.passMappings })
  })
}

export interface ReassignSamplesResult {
  movedCount: number
  aggregate: FetchWeldTelemetryAggregateResult
}

/**
 * Move specific samples from one mapped pass to another (Pass Analytics
 * "Move to pass"). Does not change upload-level zone mappings.
 */
export async function reassignSamples(input: {
  projectId: string
  pipeSetId: string
  weldId: string
  sourcePassLabel: string
  targetPassLabel: string
  sampleIds: number[]
}): Promise<ReassignSamplesResult> {
  const path = telemetryBasePath(input) + '/reassign-samples'
  return apiFetch<ReassignSamplesResult>(path, {
    method: 'POST',
    body: JSON.stringify({
      sourcePassLabel: input.sourcePassLabel,
      targetPassLabel: input.targetPassLabel,
      sampleIds: input.sampleIds
    })
  })
}

// ---------------------------------------------------------------------------
// Per-pass Min/Max overrides (Pass Analytics — Calculate min/max)
// ---------------------------------------------------------------------------

export interface MinMaxOverrideInput {
  parameter: string
  min: number | null
  avg: number | null
  max: number | null
  sampleCount: number
}

export interface MinMaxOverrideRecord extends MinMaxOverrideInput {
  updatedAt: string
}

export interface MinMaxOverridesResult {
  weldId: string
  passLabel: string
  overrides: MinMaxOverrideRecord[]
  /**
   * Sample IDs the user has explicitly UNSELECTED on this pass. Empty array
   * when the pass has never been saved (default: every row selected).
   */
  unselectedSampleIds: number[]
}

export async function fetchMinMaxOverrides(input: {
  projectId: string
  pipeSetId: string
  weldId: string
  passLabel: string
}): Promise<MinMaxOverridesResult> {
  const path =
    telemetryBasePath(input) +
    `/passes/${encodeURIComponent(input.passLabel)}/minmax`
  const result = await apiFetch<MinMaxOverridesResult>(path, { method: 'GET' })
  // Tolerate older server responses that pre-date the selection field.
  return {
    ...result,
    unselectedSampleIds: Array.isArray(result.unselectedSampleIds)
      ? result.unselectedSampleIds
      : []
  }
}

export async function saveMinMaxOverrides(input: {
  projectId: string
  pipeSetId: string
  weldId: string
  passLabel: string
  overrides: MinMaxOverrideInput[]
  /**
   * Sample IDs to record as UNSELECTED for this pass. The server replaces
   * the previously stored selection snapshot in the same transaction as the
   * Min/Avg/Max overrides.
   */
  unselectedSampleIds: number[]
}): Promise<MinMaxOverridesResult> {
  const path =
    telemetryBasePath(input) +
    `/passes/${encodeURIComponent(input.passLabel)}/minmax`
  const result = await apiFetch<MinMaxOverridesResult>(path, {
    method: 'POST',
    body: JSON.stringify({
      overrides: input.overrides,
      unselectedSampleIds: input.unselectedSampleIds
    })
  })
  return {
    ...result,
    unselectedSampleIds: Array.isArray(result.unselectedSampleIds)
      ? result.unselectedSampleIds
      : []
  }
}
